import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { productsService, ProductsResponse } from '../services/products';
import { categoriesService } from '../services/categories';
import { 
  SparklesIcon, 
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';

export const HomePage: React.FC = () => {
  const carouselRef = useRef<HTMLDivElement>(null);

  // Récupérer 8 produits pour "Notre Sélection" (demander plus pour compenser le filtrage)
  const { data: selectionProducts } = useQuery<ProductsResponse>({
    queryKey: ['products', 'homepage-selection-v2'],
    queryFn: async () => {
      const response = await productsService.getProducts({ per_page: 30 });
      return {
        ...response,
        products: response.products.slice(0, 8) // Prendre seulement les 8 premiers après filtrage
      };
    },
    staleTime: 0,
  });

  const scrollCarousel = (direction: 'left' | 'right') => {
    if (carouselRef.current) {
      // Scroll d'une carte à la fois (largeur de carte + gap)
      const scrollAmount = 344; // 320px (w-80) + 24px (gap-6)
      const newScrollPosition = carouselRef.current.scrollLeft + (direction === 'left' ? -scrollAmount : scrollAmount);
      carouselRef.current.scrollTo({
        left: newScrollPosition,
        behavior: 'smooth'
      });
    }
  };

  // Récupérer les catégories principales pour "Nos Collections"
  const { data: mainCategories } = useQuery({
    queryKey: ['categories', 'parent'],
    queryFn: () => categoriesService.getParentCategories({ per_page: 4 }),
  });

  return (
    <div className="bg-white">
      {/* Hero Section - Grande image */}
      <section className="relative h-[700px] overflow-hidden">
        {/* Image de fond optimisée pour LCP */}
        <img 
          src="/assets/images/bague-or-18-carats-diamants-aupiho-joaillerie.jpeg"
          alt="Bague en or 18 carats avec diamants - Aupiho Joaillerie"
          className="absolute inset-0 w-full h-full object-cover object-center"
          fetchPriority="high"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent"></div>
        
        {/* Contenu aligné à gauche */}
        <div className="relative h-full container mx-auto px-4 lg:px-16">
          <div className="flex items-center h-full">
            <div className="text-white z-10 max-w-2xl">
              {/* Logo AUPIHO dégradé */}
              <div className="mb-8">
                <img 
                  src="/assets/logo/Logo_aupiho_degrade-150x150.png" 
                  alt="Aupiho Logo" 
                  className="h-14 w-14 mb-6"
                />
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-light mb-6 tracking-wide leading-tight">
                AUPIHO, une bijouterie<br />
                précieuse et moderne
              </h1>
              
              <div className="space-y-2 mb-8 text-lg md:text-xl font-light text-white/90">
                <p>spécialisée en bijouterie, joaillerie et horlogerie</p>
                <p>qui conçoit des bijoux uniques et accessibles</p>
                <p>avec des pierres naturelles et de l'or recyclé.</p>
              </div>
              
              <div className="flex items-center gap-2 mb-8">
                <div className="h-px w-16 bg-[#c69c70]"></div>
                <SparklesIcon className="h-6 w-6 text-[#c69c70]" />
                <div className="h-px w-16 bg-[#c69c70]"></div>
              </div>
              
              <Link 
                to="/categories" 
                className="inline-block px-10 py-4 bg-[#c69c70] text-white hover:bg-[#b88a5f] transition-all duration-300 font-light tracking-wider text-lg shadow-xl hover:shadow-2xl"
              >
                DÉCOUVRIR NOS CRÉATIONS
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Notre Sélection */}
      <section className="py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-light text-[#c69c70] mb-6 tracking-wide">Notre Sélection</h2>
            <div className="flex items-center justify-center gap-3">
              <div className="w-20 h-px bg-[#c69c70]"></div>
              <SparklesIcon className="h-6 w-6 text-[#c69c70]" />
              <div className="w-20 h-px bg-[#c69c70]"></div>
            </div>
            <p className="text-gray-600 mt-6 text-lg font-light">Découvrez nos créations les plus précieuses</p>
          </div>
          
          {selectionProducts && selectionProducts.products.length > 0 ? (
            <div className="relative mx-auto group/carousel">
              {/* Bouton Précédent */}
              <button
                onClick={() => scrollCarousel('left')}
                className="absolute left-4 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white shadow-xl rounded-full p-3 transition-all duration-300 opacity-0 group-hover/carousel:opacity-100 hover:scale-110 border-2 border-[#c69c70]"
                aria-label="Précédent"
              >
                <ChevronLeftIcon className="h-6 w-6 text-[#c69c70]" />
              </button>

              {/* Bouton Suivant */}
              <button
                onClick={() => scrollCarousel('right')}
                className="absolute right-4 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white shadow-xl rounded-full p-3 transition-all duration-300 opacity-0 group-hover/carousel:opacity-100 hover:scale-110 border-2 border-[#c69c70]"
                aria-label="Suivant"
              >
                <ChevronRightIcon className="h-6 w-6 text-[#c69c70]" />
              </button>

              {/* Carousel Container - Affiche 3 produits */}
              <div className="max-w-[1040px] mx-auto overflow-hidden">
                <div 
                  ref={carouselRef}
                  className="overflow-x-scroll scrollbar-hide pb-8 scroll-smooth px-4"
                >
                  <div className="flex gap-6">
                    {selectionProducts.products.map((product) => (
                      <Link
                        key={product.id}
                        to={`/products/${product.slug}`}
                        className="group flex-shrink-0 w-80"
                      >
                        <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 border border-gray-100">
                          {/* Image */}
                          <div className="relative aspect-square overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100">
                            {product.images.length > 0 ? (
                              <img
                                src={product.images[0].src}
                                alt={product.name}
                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                              />
                            ) : (
                              <div className="w-full h-full bg-gradient-to-br from-[#c69c70]/20 to-[#497aab]/20 flex items-center justify-center">
                                <SparklesIcon className="h-20 w-20 text-[#c69c70]/40" />
                              </div>
                            )}
                            <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                          </div>
                          
                          {/* Contenu */}
                          <div className="p-6 bg-white">
                            <h3 className="font-light text-gray-900 text-lg leading-snug group-hover:text-[#c69c70] transition-colors duration-300 line-clamp-2 min-h-[3.5rem]">
                              {product.name}
                            </h3>
                            
                            <div className="mt-4 flex items-center justify-between">
                              <span className="text-sm text-[#497aab] font-medium group-hover:text-[#c69c70] transition-colors flex items-center gap-2">
                                Découvrir
                                <ChevronRightIcon className="h-4 w-4 transform group-hover:translate-x-1 transition-transform" />
                              </span>
                            </div>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center text-gray-500 py-12">
              <div className="inline-block animate-pulse">
                <SparklesIcon className="h-12 w-12 text-[#c69c70] mx-auto mb-4" />
                <p>Chargement des produits...</p>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Nos Collections */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-light text-[#c69c70] mb-6 tracking-wide">Nos Collections</h2>
            <div className="flex items-center justify-center gap-3">
              <div className="w-20 h-px bg-[#c69c70]"></div>
              <SparklesIcon className="h-6 w-6 text-[#c69c70]" />
              <div className="w-20 h-px bg-[#c69c70]"></div>
            </div>
            <p className="text-gray-600 mt-6 text-lg font-light">Explorez nos collections de bijoux en or et diamants</p>
          </div>
          
          <div className="max-w-7xl mx-auto">
            {/* Première ligne - 2 grandes collections */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {/* Bagues */}
              <Link
                to="/category/bagues"
                className="group relative overflow-hidden rounded-2xl h-[500px] shadow-lg hover:shadow-2xl transition-all duration-500"
              >
                <img
                  src="/assets/images/collections/bague-croix-or-jaune-diamants-blancs-aupiho-joaillerie.jpeg"
                  alt="Bagues"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                <div className="absolute inset-0 flex items-end p-10">
                  <div className="text-white">
                    <h3 className="text-4xl font-light mb-3 tracking-wide">Bagues</h3>
                    <p className="text-white/90 text-lg font-light mb-6">Symboles d'élégance et de raffinement</p>
                    <span className="inline-flex items-center gap-2 px-6 py-3 bg-[#c69c70] hover:bg-[#b88a5f] transition-colors duration-300 rounded-full text-sm font-medium tracking-wider shadow-xl">
                      Découvrir
                      <ChevronRightIcon className="h-5 w-5" />
                    </span>
                  </div>
                </div>
              </Link>

              {/* Colliers */}
              <Link
                to="/category/colliers"
                className="group relative overflow-hidden rounded-2xl h-[500px] shadow-lg hover:shadow-2xl transition-all duration-500"
              >
                <img
                  src="/assets/images/collections/collier-cle-or-blanc-diamants-blancs-aupiho-joaillerie-scaled.jpeg"
                  alt="Colliers"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                <div className="absolute inset-0 flex items-end p-10">
                  <div className="text-white">
                    <h3 className="text-4xl font-light mb-3 tracking-wide">Colliers</h3>
                    <p className="text-white/90 text-lg font-light mb-6">Sublimez votre décolleté avec grâce</p>
                    <span className="inline-flex items-center gap-2 px-6 py-3 bg-[#c69c70] hover:bg-[#b88a5f] transition-colors duration-300 rounded-full text-sm font-medium tracking-wider shadow-xl">
                      Découvrir
                      <ChevronRightIcon className="h-5 w-5" />
                    </span>
                  </div>
                </div>
              </Link>
            </div>

            {/* Deuxième ligne - 2 grandes collections */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Boucles d'oreilles */}
              <Link
                to="/category/boucles-doreilles"
                className="group relative overflow-hidden rounded-2xl h-[500px] shadow-lg hover:shadow-2xl transition-all duration-500"
              >
                <img
                  src="/assets/images/collections/boucle-doreille-or-jaune-diamants-blancs-aupiho-joaillerie.jpeg"
                  alt="Boucles d'oreilles"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                <div className="absolute inset-0 flex items-end p-10">
                  <div className="text-white">
                    <h3 className="text-4xl font-light mb-3 tracking-wide">Boucles d'oreilles</h3>
                    <p className="text-white/90 text-lg font-light mb-6">Illuminez votre visage avec style</p>
                    <span className="inline-flex items-center gap-2 px-6 py-3 bg-[#c69c70] hover:bg-[#b88a5f] transition-colors duration-300 rounded-full text-sm font-medium tracking-wider shadow-xl">
                      Découvrir
                      <ChevronRightIcon className="h-5 w-5" />
                    </span>
                  </div>
                </div>
              </Link>

              {/* Bracelets */}
              <Link
                to="/category/bracelets"
                className="group relative overflow-hidden rounded-2xl h-[500px] shadow-lg hover:shadow-2xl transition-all duration-500"
              >
                <img
                  src="/assets/images/collections/bracelet-jonc-or-jaune-diamants-blancs-pampilles-aupiho-joaillerie-scaled.jpeg"
                  alt="Bracelets"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
                <div className="absolute inset-0 flex items-end p-10">
                  <div className="text-white">
                    <h3 className="text-4xl font-light mb-3 tracking-wide">Bracelets</h3>
                    <p className="text-white/90 text-lg font-light mb-6">Ornez vos poignets avec délicatesse</p>
                    <span className="inline-flex items-center gap-2 px-6 py-3 bg-[#c69c70] hover:bg-[#b88a5f] transition-colors duration-300 rounded-full text-sm font-medium tracking-wider shadow-xl">
                      Découvrir
                      <ChevronRightIcon className="h-5 w-5" />
                    </span>
                  </div>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Notre Histoire */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-light text-[#c69c70] mb-6 tracking-wide">Notre Histoire</h2>
            <div className="flex items-center justify-center gap-3">
              <div className="w-20 h-px bg-[#c69c70]"></div>
              <SparklesIcon className="h-6 w-6 text-[#c69c70]" />
              <div className="w-20 h-px bg-[#c69c70]"></div>
            </div>
            <p className="text-gray-600 mt-6 text-lg font-light">Une passion transmise de génération en génération</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 max-w-6xl mx-auto items-center">
            <div className="order-2 md:order-1">
              <div className="space-y-6 text-gray-700 leading-relaxed text-base">
                <p>
                  <strong className="text-[#c69c70]">Aupiho</strong>, œuf l'Horloger de Besiktas, passionnée par les bijoux, dès son passage remarquée par les femmes de sa famille. 
                  Les découvertes et l'initiation aux belles parures et aux belles pierres <strong>ont façonné sa vocation</strong>.
                </p>
                
                <p>
                  <strong className="text-[#c69c70]">Sa naissance AUPIHO</strong><br />
                  Aujourd'hui, les bijoux symbolisent <strong>ses dépôts</strong>, sa haute qualité, sa noblesse <strong>et sa grandeur et sa beauté intemporelle</strong>. Avec des ans sont une une énergie de ses dédicé aux femmes elle il est d'agrémenter la vie quotidienne et ses moments de préciosité et ses réflexions individuels.
                </p>
                
                <p>
                  <strong className="text-[#c69c70]">Le savoir-faire</strong><br />
                  Grâce à l'efficacité sont et Fernand, la marque AUPIHO repose sur un <strong>savoir-faire d'artisanat</strong>, des nôtres, générations et des espèces qui sera la clé du dépassement dans une <strong>qualitiés à la française</strong>.
                </p>
                
                <p>
                  <strong className="text-[#c69c70]">La création</strong><br />
                  Maître en son art, elle allient des dessins, L'AUPIHO des bijoux, les bijoux entrapés sur une qualité artisanale par une bijoux entrapés finais des métiers pour tous ou pour qualité.
                </p>
                
                <p>
                  <strong className="text-[#c69c70]">L'éthique</strong><br />
                  Nos matières AUPIHO sont <strong>fabriquées dans le respect de l'homme</strong> et des processus environnementaux, tout en long de notre production. Aussi éthique fondamentals pour la Normandie et des professionnels des bijoux pour deux étages.
                </p>
              </div>
            </div>
            
            <div className="order-1 md:order-2">
              <div className="relative overflow-hidden rounded-2xl shadow-xl">
                <img 
                  src="/assets/images/DSC00572-1-1-1-scaled.jpg" 
                  alt="Fondatrice Aupiho" 
                  className="w-full h-full object-cover aspect-[3/4]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#c69c70]/20 to-transparent"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Découvrez l'univers */}
      <section className="relative h-[500px] overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: 'url(/assets/images/bague-or-18-carats-diamants-aupiho-joaillerie.jpeg)',
            backgroundAttachment: 'fixed'
          }}
        ></div>
        <div className="absolute inset-0 bg-black/70"></div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-white z-10 px-4 max-w-3xl">
            <div className="flex items-center justify-center gap-3 mb-6">
              <div className="w-12 h-px bg-[#c69c70]"></div>
              <SparklesIcon className="h-8 w-8 text-[#c69c70]" />
              <div className="w-12 h-px bg-[#c69c70]"></div>
            </div>
            <h2 className="text-5xl font-light mb-8 tracking-wide">
              Découvrez l'univers AUPIHO joaillerie
            </h2>
            <p className="text-xl font-light mb-10 text-white/90">
              Des créations uniques en or recyclé et pierres naturelles
            </p>
            <Link 
              to="/categories" 
              className="inline-block px-10 py-4 bg-[#c69c70] text-white hover:bg-[#b88a5f] transition-all duration-300 font-medium tracking-wider text-lg shadow-xl hover:shadow-2xl"
            >
              JE DÉCOUVRE
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

